import { Component, Input, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-contractors-janus-action',
  templateUrl: './contractors-janus-action.component.html',
  styleUrls: ['./contractors-janus-action.component.scss'],
  standalone: false,
  encapsulation: ViewEncapsulation.None
})
export class ContractorsJanusActionComponent implements OnDestroy {
  @ViewChild('actionPopover', { static: false }) private actionPopover?: NgbPopover;
  currentPlacement = 'bottom-end top-end';

  // Added by Amisha on 07/16/2026 so registrations and types can share the same action UI but pass different actions.
  @Input() row!: any;
  @Input() viewDetails!: (row: any) => void;
  @Input() viewApplication!: (row: any) => void;
  @Input() renew!: (row: any) => void;
  @Input() disable!: (row: any) => void;
  @Input() enable!: (row: any) => void;
  @Input() delete!: (row: any) => void;
  @Input() viewProfile!: (row: any) => void;

  @Input() canEnable!: (row: any) => boolean;
  @Input() canDisable!: (row: any) => boolean;
  @Input() canDelete!: (row: any) => boolean;
  @Input() canRenew!: (row: any) => boolean;

  private readonly boundClosePopoverOnResize = () => this.closePopover();

  togglePopover(popover: NgbPopover, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (popover.isOpen()) {
      popover.close();
      return;
    }

    this.updatePopoverPlacement(event.currentTarget as HTMLElement | null);
    popover.open();
  }

  onViewDetails(): void {
    this.closePopover();
    this.viewDetails?.(this.row);
  }

  onViewApplication(): void {
    this.closePopover();
    this.viewApplication?.(this.row);
  }

  onRenew(): void {
    this.closePopover();
    this.renew?.(this.row);
  }

  onDelete(): void {
    this.closePopover();
    this.delete?.(this.row);
  }

  onDisable(): void {
    this.closePopover();
    this.disable?.(this.row);
  }

  onEnable(): void {
    this.closePopover();
    this.enable?.(this.row);
  }

  onViewProfile(): void {
    this.closePopover();
    this.viewProfile?.(this.row);
  }

  onPopoverShown(): void {
    this.detachPopoverListeners();
    window.addEventListener('resize', this.boundClosePopoverOnResize, true);
  }

  onPopoverHidden(): void {
    this.detachPopoverListeners();
  }

  ngOnDestroy(): void {
    this.detachPopoverListeners();
  }

  private closePopover(): void {
    this.actionPopover?.close();
  }

  private detachPopoverListeners(): void {
    window.removeEventListener('resize', this.boundClosePopoverOnResize, true);
  }

  private updatePopoverPlacement(trigger: HTMLElement | null): void {
    if (!trigger) {
      this.currentPlacement = 'bottom-end top-end';
      return;
    }

    // Added by Amisha on 2026-07-17.
    // Reason: contractor registrations include an extra View Profile action, so the
    // floating action menu needs to open upward on lower rows when there is not enough
    // viewport space below the trigger.
    const rect = trigger.getBoundingClientRect();
    const itemCount = [
      this.viewDetails,
      this.viewApplication,
      this.renew,
      this.delete,
      this.disable,
      this.enable,
      this.viewProfile
    ].filter(Boolean).length;
    const responsiveItemHeight = Math.min(
      54,
      Math.max(38, window.innerHeight * 0.07)
    );
    const estimatedMenuHeight = itemCount * responsiveItemHeight + 16;
    const viewportPadding = 16;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;

    this.currentPlacement =
      spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow
        ? 'top-end bottom-end'
        : 'bottom-end top-end';
  }
}
